'use client';

import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import DefaultPage from '@/components/templates/DefaultPage';
import { settingAPI } from '@/lib/api';

interface ContactInfo {
  // Global Sales Hotline
  contact_global_sales_tel?: string;
  contact_global_sales_mail?: string;
  
  // Overseas Service Hotline
  contact_overseas_service_tel?: string;
  contact_overseas_service_hours?: string;
  contact_overseas_service_mail?: string;
  
  // Headquarters
  contact_headquarters_addr?: string;
  contact_headquarters_pc?: string;
  contact_headquarters_web?: string;
  contact_headquarters_tel?: string;
  contact_headquarters_fax?: string;
}

export default function ContactUsPage() {
  const [contactInfo, setContactInfo] = useState<ContactInfo>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const settings = await settingAPI.getAll();
        setContactInfo(settings as ContactInfo);
      } catch (error) {
        console.error('Failed to fetch contact info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  // 默认值（当设置未配置时使用）
  const defaults = {
    globalSalesTel: '0086-731-85835111',
    globalSalesMail: 'sales31@sanygroup.cn',
    overseasServiceTel: '4008091111/010-6911111',
    overseasServiceHours: 'Mon–Fri 8:00 AM -17:30 PM (China Standard Time)',
    overseasServiceMail: 'xxx@xxx.com.cn',
    headquartersAddr: 'SAPark, Economic and Technological Development Zone, Changsha, Hunan, China',
    headquartersPc: '410100',
    headquartersWeb: 'www.xxxx.com',
    headquartersTel: '0086-731-84053111',
    headquartersFax: '0086-731-84111111',
  };

  return (
    <Layout>
      <DefaultPage
        title="联系我们"
        subtitle="我们期待与您的合作"
        layout="two-column"
        leftColumn={
          <div className="elementor-section elementor-inner-section elementor-section-boxed">
            <div className="elementor-container elementor-column-gap-default">
              <div className="elementor-column elementor-col-100">
                <div className="elementor-widget-wrap">
                  {/* 标题 - 精确匹配参考页面 */}
                  <div className="elementor-element elementor-widget elementor-widget-heading mb-6">
                    <div className="elementor-widget-container">
                      <h1 className="elementor-heading-title elementor-size-default text-3xl font-bold">
                        Contact Us
                      </h1>
                    </div>
                  </div>
                  
                  {/* 分隔线 */}
                  <div className="elementor-element elementor-widget-divider--view-line elementor-widget elementor-widget-divider mb-6">
                    <div className="elementor-widget-container">
                      <div className="elementor-divider">
                        <span className="elementor-divider-separator" style={{
                          borderTop: '1px solid #e6e6e6',
                          display: 'block',
                          width: '100%',
                        }}></span>
                      </div>
                    </div>
                  </div>

                  {/* 联系信息内容 - 从API动态获取 */}
                  {!loading && (
                    <div className="elementor-element elementor-widget elementor-widget-theme-post-content">
                      <div className="elementor-widget-container">
                        {/* Global Sales Hotline */}
                        <div className="w-contact1">
                          <h3 className="addh3">Global Sales Hotline</h3>
                          <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                              <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                <td className="add-tel s-add-color" style={{ width: '160px', backgroundColor: '#f9f9f9', fontWeight: 'bold', color: '#666', padding: '12px 5px' }}>Tel:</td>
                                <td className="add-tel2" style={{ backgroundColor: '#fff', color: '#222', padding: '12px 5px' }}>
                                  {contactInfo.contact_global_sales_tel || defaults.globalSalesTel}
                                </td>
                              </tr>
                              <tr style={{ borderBottom: 'none' }}>
                                <td className="s-add-color" style={{ width: '160px', backgroundColor: '#f9f9f9', fontWeight: 'bold', color: '#666', padding: '12px 5px' }}>Mail:</td>
                                <td style={{ backgroundColor: '#fff', color: '#222', padding: '12px 5px' }}>
                                  {contactInfo.contact_global_sales_mail || defaults.globalSalesMail}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Overseas Service Hotline */}
                        <div className="w-contact1 w-contact3">
                          <h3 className="addh3">Overseas Service Hotline</h3>
                          <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                              <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                <td className="add-tel s-add-color" style={{ width: '160px', backgroundColor: '#f9f9f9', fontWeight: 'bold', color: '#666', padding: '12px 5px' }}>Tel:</td>
                                <td className="add-tel2" style={{ backgroundColor: '#fff', color: '#222', padding: '12px 5px' }}>
                                  {contactInfo.contact_overseas_service_tel || defaults.overseasServiceTel}
                                </td>
                              </tr>
                              <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                <td style={{ width: '160px', backgroundColor: '#f9f9f9', padding: '12px 5px' }}></td>
                                <td style={{ backgroundColor: '#fff', color: '#222', padding: '12px 5px' }}>
                                  {contactInfo.contact_overseas_service_hours || defaults.overseasServiceHours}
                                </td>
                              </tr>
                              <tr style={{ borderBottom: 'none' }}>
                                <td className="s-add-color" style={{ width: '160px', backgroundColor: '#f9f9f9', fontWeight: 'bold', color: '#666', padding: '12px 5px' }}>Mail:</td>
                                <td className="add-tel2" style={{ backgroundColor: '#fff', color: '#222', padding: '12px 5px' }}>
                                  {contactInfo.contact_overseas_service_mail || defaults.overseasServiceMail}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Headquarters (China) */}
                        <div className="w-contact1 w-adress">
                          <h3 className="addh3">Headquarters (China)</h3>
                          <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                              <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                <td className="add-tel s-add-color" style={{ width: '160px', backgroundColor: '#f9f9f9', fontWeight: 'bold', color: '#666', padding: '12px 5px' }}>Addr:</td>
                                <td className="add-tel2" style={{ backgroundColor: '#fff', color: '#222', padding: '12px 5px' }}>
                                  {contactInfo.contact_headquarters_addr || defaults.headquartersAddr}
                                </td>
                              </tr>
                              <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                <td className="s-add-color" style={{ width: '160px', backgroundColor: '#f9f9f9', fontWeight: 'bold', color: '#666', padding: '12px 5px' }}>P.C.:</td>
                                <td style={{ backgroundColor: '#fff', color: '#222', padding: '12px 5px' }}>
                                  {contactInfo.contact_headquarters_pc || defaults.headquartersPc}
                                </td>
                              </tr>
                              <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                <td className="s-add-color" style={{ width: '160px', backgroundColor: '#f9f9f9', fontWeight: 'bold', color: '#666', padding: '12px 5px' }}>Web:</td>
                                <td style={{ backgroundColor: '#fff', color: '#222', padding: '12px 5px' }}>
                                  {contactInfo.contact_headquarters_web ? (
                                    <a 
                                      href={contactInfo.contact_headquarters_web.startsWith('http') ? contactInfo.contact_headquarters_web : `https://${contactInfo.contact_headquarters_web}`}
                                      className="text-blue-600 hover:underline"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {contactInfo.contact_headquarters_web}
                                    </a>
                                  ) : (
                                    <a href={`https://${defaults.headquartersWeb}`} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                                      {defaults.headquartersWeb}
                                    </a>
                                  )}
                                </td>
                              </tr>
                              <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                <td className="s-add-color" style={{ width: '160px', backgroundColor: '#f9f9f9', fontWeight: 'bold', color: '#666', padding: '12px 5px' }}>Tel:</td>
                                <td style={{ backgroundColor: '#fff', color: '#222', padding: '12px 5px' }}>
                                  {contactInfo.contact_headquarters_tel || defaults.headquartersTel}
                                </td>
                              </tr>
                              <tr style={{ borderBottom: 'none' }}>
                                <td className="s-add-color" style={{ width: '160px', backgroundColor: '#f9f9f9', fontWeight: 'bold', color: '#666', padding: '12px 5px' }}>Fax:</td>
                                <td style={{ backgroundColor: '#fff', color: '#222', padding: '12px 5px' }}>
                                  {contactInfo.contact_headquarters_fax || defaults.headquartersFax}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
      >
        {/* children 内容（当不使用两列布局时显示） */}
      </DefaultPage>
    </Layout>
  );
}

